/**
 * GuestContext — passport + phone_last4 session for Patient Portal
 * Persists to sessionStorage so refresh doesn't lose data.
 * On mount, rehydrates from storage and re-validates via RPC.
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { t } from '../i18n/en';

const STORAGE_KEY = 'patient_portal_session';

interface GuestSession {
  passportNumber: string;
  phoneLast4: string;
  leadId: string;
  tripCaseId: string | null;
  patientName: string;
  accompanyingPersons: number;
}

type BootState = 'initializing' | 'ready';

interface GuestContextType {
  session: GuestSession | null;
  loading: boolean;
  error: string | null;
  /** 'initializing' while rehydrating from storage; 'ready' once done */
  bootState: BootState;
  lookup: (passport: string, phoneLast4: string) => Promise<boolean>;
  logout: () => void;
}

const GuestContext = createContext<GuestContextType | null>(null);

export function useGuestSession() {
  const ctx = useContext(GuestContext);
  if (!ctx) throw new Error('useGuestSession must be used within GuestProvider');
  return ctx;
}

function saveSession(s: GuestSession) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch { /* quota exceeded — ignore */ }
}

function clearSession() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
}

function loadSession(): { passport: string; phoneLast4: string } | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.passportNumber && parsed?.phoneLast4) {
      return { passport: parsed.passportNumber, phoneLast4: parsed.phoneLast4 };
    }
  } catch {}
  return null;
}

async function fetchGuest(passport: string, phoneLast4: string): Promise<GuestSession | null> {
  const { data, error } = await supabase.rpc('patient_portal_lookup', {
    _passport: passport,
    _phone_last4: phoneLast4,
  });
  if (error || !data || (data as any[]).length === 0) return null;
  const lead = (data as any[])[0];
  console.log('[GuestContext] patient_portal_lookup result:', lead);
  if (!lead.trip_case_id) {
    console.warn('[GuestContext] trip_case_id missing from RPC — migration 127 may not be applied');
  }
  return {
    passportNumber: passport,
    phoneLast4,
    leadId: lead.lead_id,
    tripCaseId: lead.trip_case_id ?? null,
    patientName: lead.patient_name || t.defaultGuestName,
    accompanyingPersons: lead.accompanying_persons ?? 0,
  };
}

export function GuestProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<GuestSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootState, setBootState] = useState<BootState>('initializing');
  const rehydrated = useRef(false);

  // On mount: rehydrate from sessionStorage
  useEffect(() => {
    if (rehydrated.current) return;
    rehydrated.current = true;

    const stored = loadSession();
    if (!stored) {
      setBootState('ready');
      return;
    }

    // Re-validate stored credentials via RPC
    fetchGuest(stored.passport, stored.phoneLast4)
      .then(result => {
        if (result) {
          setSession(result);
          saveSession(result); // refresh stored data with latest from DB
        } else {
          clearSession(); // invalid credentials — clear
        }
      })
      .catch(() => {
        clearSession();
      })
      .finally(() => {
        setBootState('ready');
      });
  }, []);

  const lookup = useCallback(async (passport: string, phoneLast4: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const normalizedPassport = passport.trim().toUpperCase();
      const normalizedPhone = phoneLast4.trim().replace(/\D/g, '');

      if (!normalizedPassport) {
        setError(t.landing.errorPassport);
        return false;
      }
      if (normalizedPhone.length !== 4) {
        setError(t.landing.errorPhone);
        return false;
      }

      const result = await fetchGuest(normalizedPassport, normalizedPhone);
      if (!result) {
        setError(t.landing.errorNotFound);
        return false;
      }

      setSession(result);
      saveSession(result);
      return true;
    } catch (e) {
      console.error('Lookup error:', e);
      setError(t.landing.errorSystem);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    setError(null);
    clearSession();
  }, []);

  return (
    <GuestContext.Provider value={{ session, loading, error, bootState, lookup, logout }}>
      {children}
    </GuestContext.Provider>
  );
}
